import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSubmitContactMessage } from "@workspace/api-client-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@workspace/ui-web/components/ui/button";
import { Input } from "@workspace/ui-web/components/ui/input";
import { Textarea } from "@workspace/ui-web/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui-web/components/ui/form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import { useToast } from "@workspace/ui-web/hooks/use-toast";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/lib/seo";

const contactSchema = z.object({
  name: z.string().min(1, "Please enter your name"),
  email: z
    .string()
    .min(1, "Please enter your email")
    .email("Please enter a valid email"),
  phone: z.string().optional(),
  subject: z.string().optional(),
  message: z.string().min(1, "Please enter a message"),
  website: z.string().optional(),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function Contact() {
  useSeo({
    title: "Contact Us | Greater Hope Service Dogs",
    description:
      "Questions about our service dog program, donating a dog, or getting involved? Send Greater Hope Service Dogs a message and our team will get back to you.",
    path: "/contact",
  });
  const { toast } = useToast();
  const submitMessage = useSubmitContactMessage();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      website: "",
    },
  });

  const isSubmitted = submitMessage.isSuccess;

  const onSubmit = (data: ContactFormValues) => {
    const phone = data.phone?.trim();
    const subject = data.subject?.trim();
    submitMessage.mutate(
      {
        data: {
          name: data.name.trim(),
          email: data.email.trim(),
          phone: phone || undefined,
          subject: subject || undefined,
          message: data.message.trim(),
          website: data.website?.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          form.reset();
        },
        onError: () => {
          toast({
            title: "Something went wrong",
            description: "We could not send your message. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Header */}
        <section className="bg-primary text-primary-foreground py-20">
          <div className="container px-4 md:px-8 max-w-4xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Contact Us
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
              Questions about our program, donating a dog, or getting involved?
              Send us a message and our team will get back to you.
            </p>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4 md:px-8 max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Mail className="h-6 w-6 text-primary" /> Send a Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isSubmitted ? (
                  <div className="py-10 text-center space-y-4">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Thank you for reaching out
                    </h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Your message has been received. We will get back to you as
                      soon as we can.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => submitMessage.reset()}
                    >
                      Send another message
                    </Button>
                  </div>
                ) : (
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="space-y-6"
                    >
                      {/* Honeypot: hidden from real users, only bots fill this in */}
                      <div
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          width: "1px",
                          height: "1px",
                          overflow: "hidden",
                        }}
                      >
                        <label htmlFor="contact-website">
                          Leave this field empty
                        </label>
                        <input
                          id="contact-website"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          {...form.register("website")}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name *</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Your full name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email *</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="you@example.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone</FormLabel>
                              <FormControl>
                                <Input placeholder="Optional" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="subject"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subject</FormLabel>
                              <FormControl>
                                <Input placeholder="Optional" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message *</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="How can we help?"
                                className="min-h-[140px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex justify-end pt-2">
                        <Button
                          type="submit"
                          size="lg"
                          disabled={submitMessage.isPending}
                        >
                          {submitMessage.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Send Message
                        </Button>
                      </div>
                    </form>
                  </Form>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
